import { StateCreator } from "zustand";
import { GlobalState } from "../globalStore";
import { ScoreThoughtDimRequest, Thought } from "@/core/fastapiClient/sdk";

type category = string; // could be more specific
type situationID = string; // could be more specific

export type CatSitCompositeKey = `${category}:${situationID}`;

export interface ThoughtsState {
  data: Record<CatSitCompositeKey, Thought[]>;
  isLoading: Record<CatSitCompositeKey, boolean>;
  error: Record<CatSitCompositeKey, string | null>;
  isInitialized: Record<CatSitCompositeKey, boolean>; // to track which keys have been init fetched (after first fetch)
  reoderedByUser?: Record<CatSitCompositeKey, string[]>; // keep track of resorted thoughts by the user
}

export type RemoveThoughtResult = {
  success: boolean;
  removedFrom?: CatSitCompositeKey;
  removedFromSituationId?: string;
  removedFromCategory?: string;
  thought?: Thought;
};

export interface ThoughtSlice {
  thoughts: ThoughtsState;
  setThoughts: (key: CatSitCompositeKey, thoughts: Thought[]) => void;
  setThoughtsLoading: (key: CatSitCompositeKey, isLoading: boolean) => void;
  setThoughtsError: (key: CatSitCompositeKey, error: string | null) => void;
  removeThought: (thoughtId: string) => RemoveThoughtResult;
  addThought: (key: CatSitCompositeKey, thought: Thought) => void;
  updateThought: (updatedThought: Thought) => Thought | undefined;
  scoreThoughtDimension: (
    request: ScoreThoughtDimRequest
  ) => Thought | undefined;
  resetThoughts: () => void;
  // sorting
  setThoughtsOrder: (key: CatSitCompositeKey, order: string[]) => void;
  // mark initialy fetched keys
  markKeyInitialized: (key: CatSitCompositeKey) => void;
  unmarkKeyInitialized: (key: CatSitCompositeKey) => void;
  isKeyInitialized: (key: CatSitCompositeKey) => boolean;
}
export const createThoughtSlice: StateCreator<
  GlobalState,
  [],
  [],
  ThoughtSlice
> = (set, get) => ({
  thoughts: {
    data: {},
    isLoading: {},
    error: {},
    isInitialized: {}, // Initialize with an empty object
  },

  setThoughts: (key, thoughts) =>
    set((state) => {
      // Add key to isInitialized if not already there
      const updatedisInitialized = {
        ...state.thoughts.isInitialized,
        [key]: true,
      };

      return {
        thoughts: {
          ...state.thoughts,
          data: {
            ...state.thoughts.data,
            [key]: thoughts,
          },
          isInitialized: updatedisInitialized,
        },
      };
    }),

  setThoughtsLoading: (key, isLoading) =>
    set((state) => ({
      thoughts: {
        ...state.thoughts,
        isLoading: {
          ...state.thoughts.isLoading,
          [key]: isLoading,
        },
      },
    })),

  setThoughtsError: (key, error) =>
    set((state) => ({
      thoughts: {
        ...state.thoughts,
        error: {
          ...state.thoughts.error,
          [key]: error,
        },
      },
    })),

  removeThought: (thoughtId) => {
    let result: RemoveThoughtResult = { success: false };

    set((state) => {
      const updatedData = { ...state.thoughts.data };
      let thoughtRemoved = false;
      let removedFromKey: CatSitCompositeKey | undefined;
      let removedFromSituationId: string | undefined;
      let removedFromCategory: string | undefined;
      let removedThought: Thought | undefined;

      // Iterate through all situation keys
      Object.keys(updatedData).forEach((keyString) => {
        const key = keyString as CatSitCompositeKey;
        const thoughtsForKey = updatedData[key] || [];

        // Find the thought to be removed (to store it for potential undo)
        const thoughtToRemove = thoughtsForKey.find((t) => t.id === thoughtId);

        if (thoughtToRemove) {
          // Store information about what's being removed
          removedFromKey = key;

          // Parse the composite key to extract category and situation ID
          const [category, situationId] = key.split(":");
          removedFromCategory = category;
          removedFromSituationId = situationId;

          removedThought = thoughtToRemove;

          // Filter out the thought with matching ID
          updatedData[key] = thoughtsForKey.filter(
            (thought) => thought.id !== thoughtId
          );

          thoughtRemoved = true;
        }
      });

      // Store the result for return
      if (thoughtRemoved) {
        result = {
          success: true,
          removedFrom: removedFromKey,
          removedFromSituationId,
          removedFromCategory,
          thought: removedThought,
        };
      }

      // Only update state if a thought was actually removed
      if (!thoughtRemoved) {
        return state;
      }

      // Return updated state
      return {
        thoughts: {
          ...state.thoughts,
          data: updatedData,
        },
      };
    });

    // Return the result
    return result;
  },

  addThought: (key, thought) =>
    set((state) => {
      // Get existing thoughts for this key or empty array
      const existingThoughts = state.thoughts.data[key] || [];

      // Don't add if thought with same ID already exists
      if (existingThoughts.some((t) => t.id === thought.id)) {
        return state;
      }

      // Return updated state with the new thought
      return {
        thoughts: {
          ...state.thoughts,
          data: {
            ...state.thoughts.data,
            [key]: [...existingThoughts, thought],
          },
        },
      };
    }),

  updateThought: (updatedThought) => {
    let originalThought: Thought | undefined;

    set((state) => {
      // Create a shallow copy of the data
      const updatedData = { ...state.thoughts.data };
      let thoughtUpdated = false;

      // Iterate through all composite keys
      Object.keys(updatedData).forEach((keyString) => {
        const key = keyString as CatSitCompositeKey;
        const thoughtsForKey = [...updatedData[key]];

        // Find the index of the thought to update
        const thoughtIndex = thoughtsForKey.findIndex(
          (t) => t.id === updatedThought.id
        );

        // If found, update that thought
        if (thoughtIndex !== -1) {
          // Store the original thought before updating
          originalThought = { ...thoughtsForKey[thoughtIndex] };

          // Update the thought
          thoughtsForKey[thoughtIndex] = {
            ...thoughtsForKey[thoughtIndex], // Keep existing properties
            ...updatedThought, // Override with updated properties
          };

          updatedData[key] = thoughtsForKey;
          thoughtUpdated = true;
        }
      });

      // If no thought was updated, return the original state
      if (!thoughtUpdated) {
        return state;
      }

      // Return updated state
      return {
        thoughts: {
          ...state.thoughts,
          data: updatedData,
        },
      };
    });

    // Return the original thought (before it was updated)
    return originalThought;
  },

  scoreThoughtDimension: (request) => {
    let updatedThought: Thought | undefined;

    set((state) => {
      // Create a shallow copy of the data
      const updatedData = { ...state.thoughts.data };

      // Iterate through all composite keys
      Object.keys(updatedData).forEach((keyString) => {
        const key = keyString as CatSitCompositeKey;
        const thoughtsForKey = [...updatedData[key]];

        // Find the thought to update
        const thoughtIndex = thoughtsForKey.findIndex(
          (t) => t.id === request.thought_id
        );

        // If found, update the dimension
        if (thoughtIndex !== -1) {
          const thoughtToUpdate = thoughtsForKey[thoughtIndex];

          // Update the specified dimension
          thoughtToUpdate[request.dimension_index] = {
            score: request.score,
            instrument: request.instrument,
          };

          // Store the updated thought for return
          updatedThought = { ...thoughtToUpdate };

          // Update the thoughts array
          thoughtsForKey[thoughtIndex] = thoughtToUpdate;
          updatedData[key] = thoughtsForKey;
        }
      });

      // Return updated state
      return {
        thoughts: {
          ...state.thoughts,
          data: updatedData,
        },
      };
    });

    // Return the updated thought
    return updatedThought;
  },

  resetThoughts: () =>
    set({
      thoughts: {
        data: {},
        isLoading: {},
        error: {},
        // isInitialized: [], // Reset initialized keys too
        isInitialized: {}, // Reset initialized keys too
      },
    }),
  // sorting
  setThoughtsOrder: (key, order) =>
    set((state) => ({
      thoughts: {
        ...state.thoughts,
        reoderedByUser: {
          ...state.thoughts.reoderedByUser,
          [key]: order,
        },
      },
    })),

  // mark initialy fetched keys
  markKeyInitialized: (key) =>
    set((state) => ({
      thoughts: {
        ...state.thoughts,
        isInitialized: {
          ...state.thoughts.isInitialized,
          [key]: true,
        },
      },
    })),

  unmarkKeyInitialized: (key) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = state.thoughts.isInitialized;
      return {
        thoughts: {
          ...state.thoughts,
          isInitialized: rest,
        },
      };
    }),

  isKeyInitialized: (key) => !!get().thoughts.isInitialized[key], // Check if the key exists in the object
});
